import { add } from 'date-fns';

import type { IPeriod, IRate } from '@src/@types/microcredits';
import { proportion } from '@src/helpers/proportion';

const MINUTES_IN_DAY = 60 * 24;
export const mapDaysToMinutes = (days: number) => days * MINUTES_IN_DAY;
export const mapMinutesToDays = (minutes: number) => Math.floor(minutes / MINUTES_IN_DAY);

const filterByAmount = (amount: IRate['amount'], value: number): boolean =>
  amount.from !== null && amount.to !== null && amount.from <= value && value <= amount.to;

const filterByTerm = (term: IPeriod['term'], value: number): boolean =>
  term.fromValue !== null && term.toValue !== null && term.fromValue <= value && value <= term.toValue;

export const getReturnCondition = (rates: IRate[], amount: number, minutes: number) => {
  // eslint-disable-next-line @typescript-eslint/init-declarations
  let result;
  for (let i = 0; i < rates.length; i++) {
    const rate = rates[i];
    if (!filterByAmount(rate.amount, amount)) {
      continue;
    }
    for (let j = 0; j < rate.periods.length; j++) {
      const period = rate.periods[j];
      if (!filterByTerm(period.term, minutes)) {
        continue;
      }
      if (period.rate.from === null) {
        continue;
      }
      const returnAmount = amount + proportion(0, 100)(0, amount)(period.rate.from) * mapMinutesToDays(minutes);
      if (returnAmount < (result?.returnAmount ?? Infinity)) {
        result = { rate: period.rate.from, returnAmount };
      }
    }
  }
  return result === undefined
    ? undefined
    : {
        returnTerm: add(new Date(), { minutes }),
        returnAmount: result.returnAmount,
        rate: result.rate,
      };
};

export const getTermByAmount = (rates: IRate[], newAmount: number, nowTerm: number): number | undefined => {
  const [newTerm] = rates
    .filter(({ amount }) => filterByAmount(amount, newAmount))
    .flatMap(({ periods }) => periods.map(({ term }) => term.fromValue ?? 0))
    .sort((a, b) => Math.abs(a - nowTerm) - Math.abs(b - nowTerm));
  return newTerm;
};
export const getAmountByTerm = (rates: IRate[], newTerm: number, nowAmount: number): number | undefined => {
  const [newAmount] = rates
    .filter(({ periods }) => periods.some(({ term }) => filterByTerm(term, newTerm)))
    .map(({ amount }) => amount.from ?? 0)
    .sort((a, b) => Math.abs(a - nowAmount) - Math.abs(b - nowAmount));
  return newAmount;
};
